import React, { JSX, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';
import { Helmet } from 'react-helmet';

import { Button, Container } from '@mui/material';

import { htmlToPdf } from '~/services/PDFService';
import { getSentEmail } from '~/services/SailthruService';

import Spinner from '../Common/Spinner';

function getWrappedHtml(html: string): string {
  // Some email content does not import a font family, which can render unreadable text in the PDF printer.
  const fallbackFontLink = `<link href="https://fonts.googleapis.com/css?family=IBM+Plex+Sans:400,600" rel="stylesheet">`;

  return `
    ${fallbackFontLink}
    <div style="max-width: 75%; margin: 0 auto; font-family: 'IBM Plex Sans', Arial, Helvetica, sans-serif;">
      ${html}
    </div>`;
}

function RenderEmailPage(): JSX.Element {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [id, setId] = useState('');
  const [isConvertingHtmlToPdf, setIsConvertingHtmlToPdf] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');
    if (!id) {
      return;
    }

    setId(id);
    getSentEmail({ id }).then((data) => setHtmlContent(data));
  }, []);

  async function handleHtmlToPdf(html: string) {
    try {
      const wrappedHtml = getWrappedHtml(html);
      setIsConvertingHtmlToPdf(true);
      const pdf = await htmlToPdf(wrappedHtml);
      await fileDownload(pdf, `${id}.pdf`);
    } catch (err) {
      setIsConvertingHtmlToPdf(false);
      enqueueSnackbar('Unable to convert to pdf', { variant: 'error' });
    } finally {
      setIsConvertingHtmlToPdf(false);
    }
  }

  if (isConvertingHtmlToPdf) {
    return <Spinner />;
  }

  const title = `View Email`;
  return (
    <Container>
      <Container maxWidth="xl">
        <Button onClick={() => handleHtmlToPdf(htmlContent)} disabled={!htmlContent || isConvertingHtmlToPdf}>
          Download as PDF
        </Button>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </Container>
    </Container>
  );
}

export default RenderEmailPage;
